import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useModules } from '../../lib/useModules';
import {
  X,
  Save,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  Eye,
  Edit3,
  Columns,
  Wand2,
  Code,
  Zap,
  Info,
  RotateCcw,
  Sparkles,
  FileText
} from 'lucide-react';

const RECOMMENDED_TEMPLATES = {
  master: `# ROL Y OBJETIVO
<rol>
Eres un asistente virtual experto en atención al cliente para nuestra empresa. Tu objetivo es calificar a los interesados, responder preguntas con precisión y agendar una llamada con un asesor si el cliente cumple los criterios.
</rol>

# CONTEXTO DE LA EMPRESA
<contexto>
Ofrecemos soluciones tecnológicas integrales. 
Tasa Dólar BCV: {tasa_bcv} | Euro: {tasa_bcv_eur}
Fecha de hoy: {fecha_actual}
</contexto>

# REGLAS Y RESTRICCIONES
<restricciones>
- Mantén siempre un tono profesional, cortés y cercano.
- No compartas información confidencial ni hagas promesas de descuentos no autorizados.
- Si el cliente solicita hablar con un humano o no entiendes la consulta tras 2 intentos, responde adecuadamente.
</restricciones>

# EJEMPLOS DE DIÁLOGO
<ejemplos>
Usuario: ¿Cuál es el costo del servicio?
Asistente: Con gusto te brindo la información. Nuestros planes inician desde $50 al cambio en Bs. según la tasa oficial BCV de hoy ({tasa_bcv}). ¿Te gustaría conocer el detalle de cada plan?
</ejemplos>`,

  media: `# INSTRUCCIONES PARA PROCESAMIENTO MULTIMEDIA

<instrucciones_imagen>
Cuando el cliente envíe un comprobante de pago o imagen de producto:
1. Verifica que los datos sean legibles (monto, número de referencia, banco emisor).
2. Responde confirmando los datos detectados.
3. Si la imagen es borrosa o irrelevante, solicita amablemente una nueva captura clara.
</instrucciones_imagen>

<instrucciones_audio>
Cuando recibas un mensaje de voz:
1. Escucha con atención y extrae la intención principal del cliente.
2. Si el audio tiene mucho ruido o es totalmente inaudible, responde exactamente: [ESCALAR_A_HUMANO].
</instrucciones_audio>`
};

const SYSTEM_VARIABLES = [
  { tag: '{tasa_bcv}', label: 'Tasa Dólar BCV', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  { tag: '{tasa_bcv_eur}', label: 'Tasa Euro BCV', color: 'bg-teal-500/10 text-teal-600 border-teal-500/20' },
  { tag: '{fecha_actual}', label: 'Fecha Actual', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
  { tag: '{hora_actual}', label: 'Hora Actual', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  { tag: '{dia_semana}', label: 'Día de Semana', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' }
];

const XML_TAGS = [
  { tag: 'rol', label: '<rol>', wrap: true },
  { tag: 'contexto', label: '<contexto>', wrap: true },
  { tag: 'restricciones', label: '<restricciones>', wrap: true },
  { tag: 'ejemplos', label: '<ejemplos>', wrap: true },
  { tag: 'instrucciones_imagen', label: '<instrucciones_imagen>', wrap: true },
  { tag: 'instrucciones_audio', label: '<instrucciones_audio>', wrap: true }
];

const PromptEditorModal = ({
  isOpen,
  onClose,
  title = "Editor Maximizado de Prompt",
  promptType = "master", // 'master' | 'media'
  value = "",
  onChange,
  onSave,
  saving = false
}) => {
  const { isFeatureEnabled } = useModules();
  // Mismo criterio que en AiStrategySettings: si Tasas BCV está apagado, esos
  // placeholders no se reemplazan por nada, así que no se ofrecen.
  const systemVariables = isFeatureEnabled('settings', 'bcv')
    ? SYSTEM_VARIABLES
    : SYSTEM_VARIABLES.filter((v) => !v.tag.startsWith('{tasa_bcv'));

  const [text, setText] = useState(value);
  const [viewMode, setViewMode] = useState('split'); // 'edit' | 'preview' | 'split'
  const [copied, setCopied] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setText(value);
    setIsDirty(false);
  }, [value, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setText(e.target.value);
    setIsDirty(true);
    if (onChange) onChange(e.target.value);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const insertAtCursor = (textToInsert) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setText((prev) => prev + textToInsert);
      setIsDirty(true);
      if (onChange) onChange(text + textToInsert);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = text.substring(0, start) + textToInsert + text.substring(end);
    
    setText(newText);
    setIsDirty(true);
    if (onChange) onChange(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
    }, 50);
  };

  const wrapSelectionWithTag = (tagName) => {
    const textarea = textareaRef.current;
    const startTag = `<${tagName}>\n`;
    const endTag = `\n</${tagName}>`;

    if (!textarea) {
      insertAtCursor(`${startTag}${endTag}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = text.substring(start, end);
    const replacement = `${startTag}${selectedText || '...\n'}${endTag}`;

    const newText = text.substring(0, start) + replacement + text.substring(end);
    setText(newText);
    setIsDirty(true);
    if (onChange) onChange(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startTag.length, start + startTag.length + (selectedText.length || 3));
    }, 50);
  };

  const handleApplyTemplate = () => {
    const template = RECOMMENDED_TEMPLATES[promptType] || RECOMMENDED_TEMPLATES.master;
    if (text.trim() && !window.confirm("¿Deseas reemplazar el contenido actual con la estructura recomendada para IA?")) {
      return;
    }
    setText(template);
    setIsDirty(true);
    if (onChange) onChange(template);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      insertAtCursor('  ');
    }
  };

  const handleSaveClick = async () => {
    if (onSave) {
      await onSave(text);
      setIsDirty(false);
    }
  };

  const lineCount = text ? text.split('\n').length : 0;
  const charCount = text ? text.length : 0;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-lg flex flex-col justify-between p-3 md:p-6 animate-in fade-in duration-200">
      
      {/* Container Card */}
      <div className="w-full h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative">
        
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
              <Sparkles size={22} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                {title}
                {isDirty && (
                  <span className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold px-2 py-0.5 rounded-full border border-amber-500/20 uppercase tracking-wider">
                    Cambios sin guardar
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Edición estructurada con formato optimizado para LLM
              </p>
            </div>
          </div>

          {/* Center Mode Switcher */}
          <div className="flex items-center bg-slate-200/60 dark:bg-slate-800/80 p-1 rounded-2xl border border-slate-300/40 dark:border-white/5 text-xs font-bold">
            <button
              onClick={() => setViewMode('edit')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl transition-all ${
                viewMode === 'edit'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Edit3 size={14} /> Editar
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`hidden md:flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl transition-all ${
                viewMode === 'split'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Columns size={14} /> Dividido
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl transition-all ${
                viewMode === 'preview'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Eye size={14} /> Vista Previa
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-2.5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-all border border-slate-200 dark:border-slate-800 flex items-center gap-1.5 text-xs font-bold"
              title="Copiar prompt"
            >
              {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
              <span className="hidden sm:inline">{copied ? 'Copiado' : 'Copiar'}</span>
            </button>

            <button
              onClick={handleSaveClick}
              disabled={saving}
              className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-primary/25 active:scale-95 disabled:opacity-50"
            >
              <Save size={16} />
              {saving ? 'Guardando...' : 'Guardar Prompt'}
            </button>

            <button
              onClick={onClose}
              className="p-2.5 text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Assistant Toolbar */}
        <div className="px-6 py-3 bg-slate-100/70 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800/80 flex flex-wrap items-center justify-between gap-3 overflow-x-auto">
          {/* Quick Insert Variables */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mr-1 flex items-center gap-1">
              <Zap size={12} className="text-amber-500" /> Variables:
            </span>
            {systemVariables.map((v) => (
              <button
                key={v.tag}
                onClick={() => insertAtCursor(v.tag)}
                className={`px-2.5 py-1 rounded-lg text-xs font-mono font-bold border transition-all hover:scale-105 active:scale-95 ${v.color}`}
                title={`Insertar variable ${v.label}`}
              >
                {v.tag}
              </button>
            ))}
          </div>

          {/* Quick Insert XML Tags & Templates */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mr-1 flex items-center gap-1">
              <Code size={12} className="text-purple-500" /> Bloques XML:
            </span>
            {XML_TAGS.map((t) => (
              <button
                key={t.tag}
                onClick={() => wrapSelectionWithTag(t.tag)}
                className="px-2 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-300 border border-purple-500/20 hover:bg-purple-500/20 rounded-lg text-xs font-mono font-semibold transition-all hover:scale-105"
              >
                {t.label}
              </button>
            ))}

            <button
              onClick={handleApplyTemplate}
              className="ml-2 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all hover:scale-105"
            >
              <Wand2 size={13} /> Plantilla Recomendada
            </button>
          </div>
        </div>

        {/* Content Workspace */}
        <div className="flex-1 overflow-hidden relative bg-slate-900/5 dark:bg-black/40">
          <div className="w-full h-full grid grid-cols-1 overflow-hidden">
            
            {/* Split / Edit Layout */}
            <div className={`h-full flex ${viewMode === 'split' ? 'flex-col lg:flex-row' : 'flex-col'} overflow-hidden`}>
              
              {/* Textarea Editor Column */}
              {(viewMode === 'edit' || viewMode === 'split') && (
                <div className={`h-full flex flex-col ${viewMode === 'split' ? 'w-full lg:w-1/2 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800' : 'w-full'}`}>
                  <div className="px-4 py-2 bg-slate-200/40 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center text-[11px] font-bold text-slate-500">
                    <span className="flex items-center gap-1.5"><Edit3 size={13} /> EDITOR DE TEXTO</span>
                    <span>Soporta tabulaciones y sintaxis Markdown/XML</span>
                  </div>
                  <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe aquí las instrucciones detalladas para el modelo de IA..."
                    className="w-full flex-1 p-6 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-mono text-sm leading-relaxed focus:outline-none resize-none overflow-y-auto"
                    spellCheck="false"
                  />
                </div>
              )}

              {/* Preview Column */}
              {(viewMode === 'preview' || viewMode === 'split') && (
                <div className={`h-full flex flex-col ${viewMode === 'split' ? 'w-full lg:w-1/2' : 'w-full'}`}>
                  <div className="px-4 py-2 bg-slate-200/40 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center text-[11px] font-bold text-slate-500">
                    <span className="flex items-center gap-1.5"><Eye size={13} /> VISTA PREVIA RENDERIZADA</span>
                    <span>Formato visual para revisión</span>
                  </div>
                  <div className="w-full flex-1 p-6 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 overflow-y-auto prose dark:prose-invert max-w-none prose-headings:font-bold prose-h1:text-xl prose-h2:text-lg prose-p:text-sm prose-code:text-xs prose-code:font-mono">
                    {text.trim() ? (
                      <ReactMarkdown>{text}</ReactMarkdown>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-slate-400 font-medium py-12">
                        <FileText size={40} className="mb-2 opacity-40" />
                        <p>Sin contenido para mostrar en la vista previa</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer Bar */}
        <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/90 flex flex-wrap items-center justify-between gap-4 text-xs font-medium text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-6">
            <span>Líneas: <strong className="text-slate-900 dark:text-white">{lineCount}</strong></span>
            <span>Caracteres: <strong className="text-slate-900 dark:text-white">{charCount}</strong></span>
            <span className="hidden sm:inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
              <Check size={14} /> Estructura óptima para IA
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold transition-all"
            >
              Cancelar / Cerrar
            </button>
            <button
              onClick={handleSaveClick}
              disabled={saving}
              className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all flex items-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              <Save size={15} />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PromptEditorModal;
